import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  MenuItem,
  Typography,
} from '@mui/material';
import PageContainer from '../../components/container/PageContainer';
import Navbar from '../../layouts/full-layout/header/homeHeader';
import CustomFormLabel from '../../components/forms/custom-elements/CustomFormLabel';
import CustomSelect from '../../components/forms/custom-elements/CustomSelect';
import TableComponent from '../../components/table';
import { getHgtHgs2026List } from '../../api';
import {
  getAllStates,
  getDistrictsByStateCode,
  getULBsByDistrictCode,
} from '../../firebase/functions';
import { COUNT_FIELDS } from '../hgthgs2026/schema';

// Columns of the public table, in the order the reporting format asks for them.
// The short labels are reused from the form schema so the two never drift.
const TABLE_HEADERS = {
  state_name: 'State',
  district_name: 'District',
  ulb_name: 'ULB',
  offices_participation: 'Offices participated',
  number_of_offices_participated: 'No. of offices participated',
  ...Object.fromEntries(COUNT_FIELDS.map((f) => [f.name, f.short])),
};

const byLabel = (a, b) => a.label.localeCompare(b.label);

const NewReportsV2 = () => {
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [ulbs, setUlbs] = useState([]);

  const [stateCode, setStateCode] = useState('');
  const [districtCode, setDistrictCode] = useState('');
  const [ulbCode, setUlbCode] = useState('');

  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const data = await getAllStates();
      if (cancelled) return;
      setStates(
        (data || []).map((s) => ({ label: s.state_name, value: s.state_code })).sort(byLabel)
      );
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Each level of the hierarchy clears the ones below it, so a stale district
  // can never be sent alongside a newly picked state.
  const handleStateChange = async (value) => {
    setStateCode(value);
    setDistrictCode('');
    setUlbCode('');
    setDistricts([]);
    setUlbs([]);

    if (!value) return;

    const data = await getDistrictsByStateCode(value);
    setDistricts(
      (data || []).map((d) => ({ label: d.district_name, value: d.district_code })).sort(byLabel)
    );
  };

  const handleDistrictChange = async (value) => {
    setDistrictCode(value);
    setUlbCode('');
    setUlbs([]);

    if (!value) return;

    const data = await getULBsByDistrictCode(value);
    setUlbs((data || []).map((u) => ({ label: u.ulb_name, value: u.ulb_code })).sort(byLabel));
  };

  const handleReset = () => {
    setStateCode('');
    setDistrictCode('');
    setUlbCode('');
    setDistricts([]);
    setUlbs([]);
  };

  // Refetch whenever a filter changes. With no filter selected this returns the
  // whole country, which is what the page shows on first load.
  useEffect(() => {
    let cancelled = false;

    const fetchReports = async () => {
      setIsLoading(true);
      setError('');

      try {
        const response = await getHgtHgs2026List({ stateCode, districtCode, ulbCode });
        if (cancelled) return;
        setRows(response?.data?.data?.reports || []);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to fetch HGT-HGS 2026 reports:', err);
        setRows([]);
        setError('Could not load reports. Please try again.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchReports();

    return () => {
      cancelled = true;
    };
  }, [stateCode, districtCode, ulbCode]);

  return (
    <PageContainer title="Reports" description="Har Ghar Tiranga - Har Ghar Swachhata 2026 reports">
      <Navbar />
      <Box sx={{ mt: '70px', p: { xs: 2, md: 4 } }}>
        <Typography variant="h2" textAlign="center" mb={1}>
          Har Ghar Tiranga – Har Ghar Swachhata 2026
        </Typography>
        <Typography variant="h5" fontWeight="400" color="textSecondary" textAlign="center" mb={3}>
          ULB Reporting Summary
        </Typography>

        <Card>
          <CardContent>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} md={3}>
                <CustomFormLabel sx={{ mt: 0 }}>State</CustomFormLabel>
                <CustomSelect
                  value={stateCode}
                  onChange={(e) => handleStateChange(e.target.value)}
                  id="state_code"
                  name="state_code"
                  fullWidth
                  size="small"
                  displayEmpty
                >
                  <MenuItem value="">All States</MenuItem>
                  {states.map((s) => (
                    <MenuItem value={s.value} key={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </CustomSelect>
              </Grid>

              <Grid item xs={12} md={3}>
                <CustomFormLabel sx={{ mt: 0 }}>District</CustomFormLabel>
                <CustomSelect
                  value={districtCode}
                  onChange={(e) => handleDistrictChange(e.target.value)}
                  id="district_code"
                  name="district_code"
                  fullWidth
                  size="small"
                  displayEmpty
                  disabled={!stateCode}
                >
                  <MenuItem value="">All Districts</MenuItem>
                  {districts.map((d) => (
                    <MenuItem value={d.value} key={d.value}>
                      {d.label}
                    </MenuItem>
                  ))}
                </CustomSelect>
              </Grid>

              <Grid item xs={12} md={3}>
                <CustomFormLabel sx={{ mt: 0 }}>ULB</CustomFormLabel>
                <CustomSelect
                  value={ulbCode}
                  onChange={(e) => setUlbCode(e.target.value)}
                  id="ulb_code"
                  name="ulb_code"
                  fullWidth
                  size="small"
                  displayEmpty
                  disabled={!districtCode}
                >
                  <MenuItem value="">All ULBs</MenuItem>
                  {ulbs.map((u) => (
                    <MenuItem value={u.value} key={u.value}>
                      {u.label}
                    </MenuItem>
                  ))}
                </CustomSelect>
              </Grid>

              <Grid item xs={12} md={3}>
                <CustomFormLabel sx={{ mt: 0 }}>&nbsp;</CustomFormLabel>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  fullWidth
                  size="small"
                  sx={{ height: '40px' }}
                  disabled={!stateCode && !districtCode && !ulbCode}
                >
                  Reset Filters
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mt: 3 }}>
          <CardContent>
            {isLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
              </Box>
            ) : error ? (
              <Typography variant="h4" color="error" textAlign="center" py={4}>
                {error}
              </Typography>
            ) : rows.length > 0 ? (
              <>
                <Typography variant="h5" fontWeight="500" mb={2}>
                  {rows.length} {rows.length === 1 ? 'ULB has' : 'ULBs have'} reported
                </Typography>
                <Box sx={{ width: '100%', overflow: 'auto' }}>
                  <TableComponent
                    data={rows}
                    mainHeaders={TABLE_HEADERS}
                    fileName="hgt-hgs-2026-reports"
                  />
                </Box>
              </>
            ) : (
              <Typography variant="h4" color="textSecondary" textAlign="center" py={4}>
                No reports submitted yet for the selected filters.
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    </PageContainer>
  );
};

export default NewReportsV2;
